import React, {useCallback, useEffect, useRef} from "react";
import {ModalProgress} from "../../../common/modal/ModalProgress";
import {useDefaultQuery} from "../../../client/useDefaultQuery";
import {PersonDto} from "../../../client/person/PersonApiClient";
import {RobAction} from "./PersonActionlPanel";
import {useClientLocator} from "../../../client/ApiClientLocator";
import {CommonTranslKey} from "../../../common/CommonTranslKey";
import {PersonTranslKey} from "../PersonTranslKey";
import {useSnackbar} from "@dain/mui-components";
import {useTranslation} from "react-i18next";
import PersonActionStatusChecker from "./PersonActionStatusChecker";

const ACTION_TIMEOUT_MS = 120_000;

interface PersonActionStatusProps {
    person: PersonDto;
    activeRobAction: RobAction;
    setActiveRobAction: (robAction: RobAction) => void;
    setIsLoading: (isLoading: boolean) => void;
    onActionComplete: () => void;
}

export function PersonActionStatus(props: PersonActionStatusProps) {
    const {person, activeRobAction, setActiveRobAction, setIsLoading, onActionComplete} = props;
    const {t} = useTranslation();
    const {personClient} = useClientLocator();
    const snackbar = useSnackbar();

    const {isLoading: isQueryLoading, isError, error} = useDefaultQuery({
        queryKey: ["person", person.id, activeRobAction],
        queryFn: () => new Promise<PersonDto>((resolve, reject) => {
            personClient.findById(person.id)
                .then((foundPerson) => {
                    if (!foundPerson) {
                        return reject(new Error("No person found for the given ID."));
                    }

                    if (checkRobActionCondition(activeRobAction, person, foundPerson)) {
                        onActionSuccess();
                        return resolve(foundPerson);
                    }

                    return reject(new Error("Condition not met yet."));
                })
        }),
        retry: 10,
        staleTime: 0,
    });

    const onActionError = useCallback(() => {
        snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error);
        setActiveRobAction(RobAction.NO_ACTION);
    }, [snackbar, t, error, setActiveRobAction]);

    const onActionTimeout = useCallback(() => {
        snackbar.warning(t(PersonTranslKey.ACTION_STILL_IN_PROGRESS));
        setActiveRobAction(RobAction.NO_ACTION);
        setIsLoading(false);
    }, [snackbar, t, setActiveRobAction, setIsLoading]);

    const onActionTimeoutRef = useRef(onActionTimeout);
    onActionTimeoutRef.current = onActionTimeout;

    function onActionSuccess(): void {
        snackbar.success(t(PersonTranslKey.ACTION_SUCCESSFULL));
        setActiveRobAction(RobAction.NO_ACTION);
        setIsLoading(false);
        onActionComplete();
    }

    useEffect(() => {
        if (isError && error) {
            onActionError();
        }
    }, [onActionError, isError, error]);

    useEffect(() => {
        const timer = setTimeout(() => onActionTimeoutRef.current(), ACTION_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, []);

    function checkRobActionCondition(robAction: RobAction, previousRecord: PersonDto, latestRecord: PersonDto): boolean {
        switch (robAction) {
            case RobAction.FORCE_SYNC_ROB:
                return PersonActionStatusChecker.isFoundPersonRobUpdated(previousRecord, latestRecord);
            case RobAction.SUBSCRIBE:
                return PersonActionStatusChecker.isFoundPersonCorrectlySubscribed(latestRecord);
            case RobAction.UNSUBSCRIBE:
                return PersonActionStatusChecker.isFoundPersonCorrectlyUnsubscribed(latestRecord);
            default:
                return false;
        }
    }

    return (
        <>
            {isQueryLoading && <ModalProgress inProgress={isQueryLoading} />}
        </>
    );
}
